// Pure classification for the end-of-interview transcript-reconciliation gate
// (QuestionsCompleteGate). No React, no network — testable in isolation. The
// backend (interview-session-status) returns raw per-answer transcript facts; the
// "is this pending stale?" TIME judgment lives HERE, on the client, per the
// dumb-data-read contract (the endpoint does no staleness logic).

// A 'pending' whose most-recent STT attempt started longer ago than this is
// treated as STUCK (STT is fire-and-forget and can wedge); a younger pending is
// still legitimately in-flight and must not cry wolf at the interstitial.
export const STALE_PENDING_MS = 150_000;
// Bounded poll while answers are still transcribing at the interstitial.
export const POLL_INTERVAL_MS = 5_000;
export const MAX_POLL_MS = 30_000;
// Recording-attempt cap — mirrors interview-answer-upload's server cap (3) and
// QuestionFlow's MAX_ANSWER_ATTEMPTS. Defined locally: that constant is not
// exported and Stage B does not touch QuestionFlow.tsx.
export const MAX_ANSWER_ATTEMPTS = 3;

export type AnswerTranscript = {
  question_id: string;
  transcript_status: "pending" | "success" | "needs_retry" | "failed";
  transcript_error: string | null;
  transcript_requested_at: string | null;
  transcript_attempt_count: number;
  attempt_number: number;
};

export type ReconcileState =
  | "read_failed"
  | "all_clear"
  | "still_processing"
  | "needs_review"
  | "attempts_exhausted";

// Pure. Evaluated in priority order:
//   1. read failed          -> "read_failed"  (fail open; never trap the subject)
//   2. any UNRESOLVED-hard   -> "attempts_exhausted" if any such row has used up
//      (needs_retry/failed,     its re-record attempts, else "needs_review"
//       or STALE pending)
//   3. any fresh pending     -> "still_processing"
//   4. else (all resolved,   -> "all_clear"
//       or empty)
export function classifyReconcileState(
  answerTranscripts: AnswerTranscript[],
  answerTranscriptsError: boolean,
  nowMs: number,
): ReconcileState {
  if (answerTranscriptsError) return "read_failed";

  let anyUnresolvedHard = false;
  let anyExhausted = false;
  let anyInFlight = false;

  for (const a of answerTranscripts) {
    if (a.transcript_status === "success") continue; // resolved

    let unresolvedHard = false;
    if (a.transcript_status === "needs_retry" || a.transcript_status === "failed") {
      unresolvedHard = true;
    } else if (a.transcript_status === "pending") {
      const requestedMs = a.transcript_requested_at
        ? Date.parse(a.transcript_requested_at)
        : NaN;
      // Stale ONLY if provable: a parseable requested_at older than the window.
      // Null/unparseable requested_at -> can't prove stale -> treat as in-flight.
      if (!Number.isNaN(requestedMs) && nowMs - requestedMs > STALE_PENDING_MS) {
        unresolvedHard = true;
      } else {
        anyInFlight = true;
      }
    }

    if (unresolvedHard) {
      anyUnresolvedHard = true;
      // "retries used up" == the SUBJECT has burned all recording attempts on this
      // answer. We key off attempt_number (the 1..MAX recording-attempt counter),
      // NOT transcript_attempt_count: the latter counts STT INVOCATIONS (ops can
      // re-invoke server-side; no cap tied to MAX_ANSWER_ATTEMPTS), whereas
      // attempt_number is exactly what bounds whether the subject could ever
      // re-record it — the meaningful "we must follow up" trigger.
      if (a.attempt_number >= MAX_ANSWER_ATTEMPTS) anyExhausted = true;
    }
  }

  if (anyUnresolvedHard) return anyExhausted ? "attempts_exhausted" : "needs_review";
  if (anyInFlight) return "still_processing";
  return "all_clear";
}
